import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Calendar, Loader2, Clock, Sparkles, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

const STATUS_META = {
  current: { label: "In corso", icon: Clock, color: "#4A6B53" },
  upcoming: { label: "In arrivo", icon: Sparkles, color: "#C8795A" },
  past: { label: "Realizzato", icon: CheckCircle2, color: "#7A7369" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => navigate("/progetti"));
  }, [id, navigate]);

  useEffect(() => {
    if (project?.linked_companies?.length) {
      api.get("/companies").then((r) => {
        setCompanies(r.data.filter((c) => project.linked_companies.includes(c.id)));
      });
    }
  }, [project]);

  if (!project) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#4A6B53]" size={28}/>
      </div>
    );
  }

  const meta = STATUS_META[project.status] || STATUS_META.current;
  const paragraphs = (project.content || "").split(/\n{2,}/).filter(Boolean);

  return (
    <div className="bg-[#FBF9F6]">
      <div className="relative">
        {project.cover_image && (
          <div className="h-72 md:h-[440px] w-full overflow-hidden">
            <img src={imgSrc(project.cover_image)} alt={project.title} className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-b from-[#2C312E]/20 to-[#2C312E]/60"/>
          </div>
        )}
        <div className="mx-auto max-w-4xl px-6 md:px-10 -mt-40 md:-mt-52 relative">
          <Link to="/progetti" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-semibold mb-6" data-testid="back-to-projects">
            <ArrowLeft size={16}/> Torna ai progetti
          </Link>
          <div className="rounded-[2rem] bg-white border border-[#E5DCD0] p-8 md:p-12 shadow-sm">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
                style={{ background: `${meta.color}15`, color: meta.color }}
              >
                <meta.icon size={12}/> {meta.label}
              </span>
              {(project.start_date || project.end_date) && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[#7A7369] font-semibold">
                  <Calendar size={12}/>
                  {project.start_date}{project.end_date && ` — ${project.end_date}`}
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl md:text-5xl text-[#2C312E] leading-tight">{project.title}</h1>
            <p className="mt-4 text-lg text-[#58625B] leading-relaxed">{project.excerpt}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 md:px-10 py-14">
        <article className="prose-agri space-y-5">
          {paragraphs.map((p, i) => (
            <p key={`${p.slice(0, 20)}-${i}`} className="text-[#2C312E] leading-relaxed text-lg whitespace-pre-line">
              {p}
            </p>
          ))}
        </article>

        {project.gallery?.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-extrabold text-2xl text-[#2C312E] mb-5">Galleria</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {project.gallery.map((g, i) => (
                <div key={`${g}-${i}`} className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F2EBE1]">
                  <img src={imgSrc(g)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                </div>
              ))}
            </div>
          </div>
        )}

        {companies.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-extrabold text-2xl text-[#2C312E] mb-5">Fattorie coinvolte</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {companies.map((c) => (
                <Link
                  key={c.id}
                  to={`/fattorie/${c.id}`}
                  className="flex items-center gap-4 rounded-2xl bg-white border border-[#E5DCD0] p-4 hover:border-[#C8795A]/40 hover:shadow-md transition-all"
                >
                  <img src={imgSrc(c.cover_image)} alt="" className="w-16 h-16 rounded-xl object-cover"/>
                  <div>
                    <div className="font-display font-black text-[#2C312E]">{c.name}</div>
                    <div className="text-sm text-[#58625B]">{c.city} ({c.province})</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
